import React, { useState, useEffect, useRef } from 'react';
import { groupBy } from 'lodash';
import apiService from '../services/apiService';
import FileUpload from './FileUpload';
import { getColumn } from '../config/columnMapping';
import { getVatDivisor, COUNTRY_TO_MARKETPLACE } from '../config/currencies';
import {
  COUNTRIES,
  DEFAULT_COUNTRY,
  ALL_COUNTRIES_FILTER,
  getCompositeKey,
  parseCompositeKey,
  getCountryFromKey,
  getSkuFromKey,
  migrateDataToComposite,
  getCountryInfo,
  getMarginColor
} from '../utils/countryUtils';

const ProfitCalculator = () => {
  const [groupedTransactions, setGroupedTransactions] = useState([]);

  // États pour les données de coûts (avec clés composites pays_sku)
  const [productCosts, setProductCosts] = useState({});
  const [shippingCosts, setShippingCosts] = useState({});
  const [supplyCosts, setSupplyCosts] = useState({});
  const [commissionRates, setCommissionRates] = useState({});
  const [fulfillmentTypes, setFulfillmentTypes] = useState({});

  // États pour la gestion multi-pays
  const [selectedCountryFilter, setSelectedCountryFilter] = useState(ALL_COUNTRIES_FILTER);
  const [showDetailedTable, setShowDetailedTable] = useState(true);

  const [hasData, setHasData] = useState(false);
  const [reportDate, setReportDate] = useState("");
  const [importPeriod, setImportPeriod] = useState(null); // { startDate, endDate } ISO
  const [isLoading, setIsLoading] = useState(false);
  const [dbFilter, setDbFilter] = useState({ mode: 'yesterday', start: '', end: '' });
  const [dbEmptyMessage, setDbEmptyMessage] = useState(null);

  // État pour gérer l'import additionnel de pays
  const [showAdditionalImport, setShowAdditionalImport] = useState(false);

  // Ref pour l'input de fichier d'import
  const fileInputRef = useRef(null);

  // Debounce timers pour éviter les transactions SQLite concurrentes sur chaque frappe
  const debounceTimers = useRef({});

  const debounceApiCall = (key, fn, delay = 500) => {
    if (debounceTimers.current[key]) {
      clearTimeout(debounceTimers.current[key]);
    }
    debounceTimers.current[key] = setTimeout(fn, delay);
  };

  // Initialize API service and load data
  useEffect(() => {
    const initializeApp = async () => {
      try {
        setIsLoading(true);
        await apiService.initialize();
        await loadConfiguration();
      } catch (error) {
        console.error('Failed to initialize app:', error);
        // Fallback to localStorage is handled by apiService
      } finally {
        setIsLoading(false);
      }
    };
    
    initializeApp();
  }, []);
  
  const loadConfiguration = async () => {
    try {
      const config = await apiService.getConfiguration();

      // Migrer les données vers le format composite si nécessaire
      const { migratedData: migratedProductCosts } = migrateDataToComposite(config.productCosts || {});
      const { migratedData: migratedShippingCosts } = migrateDataToComposite(config.shippingCosts || {});
      const { migratedData: migratedSupplyCosts } = migrateDataToComposite(config.supplyCosts || {});
      const { migratedData: migratedCommissionRates } = migrateDataToComposite(config.commissionRates || {});
      const { migratedData: migratedFulfillmentTypes } = migrateDataToComposite(config.fulfillmentTypes || {});

      setProductCosts(migratedProductCosts);
      setShippingCosts(migratedShippingCosts);
      setSupplyCosts(migratedSupplyCosts);
      setCommissionRates(migratedCommissionRates);
      setFulfillmentTypes(migratedFulfillmentTypes);
    } catch (error) {
      console.error('Failed to load configuration:', error);
    }
  };

  const handleDataLoaded = (data, fileName, selectedCountry) => {
    const skuKey = (row) => getColumn(row, 'sku') || row['SKU'] || '';
    const grouped = Object.entries(groupBy(data, skuKey)).map(([sku, transactions]) => {
      const totalCa = transactions.reduce((sum, t) => sum + (t['Ventes de produits commandés'] || 0), 0);
      const totalUnits = transactions.reduce((sum, t) => sum + (t['Unités commandées'] || 0), 0);
      const totalOrders = transactions.reduce((sum, t) => sum + (t['Total de produits commandés'] || 0), 0);

      // Calculer le prix unitaire
      const prixUnitaire = totalUnits > 0 ? totalCa / totalUnits : 0;

      return {
        sku,
        country: selectedCountry, // Attacher le pays à chaque groupe
        title: getColumn(transactions[0], 'title') || '',
        totalCa,
        totalUnits,
        totalOrders,
        prixUnitaire,
        transactions
      };
    });

    // Extraire la date à partir du nom du fichier si possible
    let dateStr = "";
    if (fileName) {
      const match = fileName.match(/BusinessReport.*(\d{1,2})-(\d{1,2})-(\d{2,4}).*\.csv/);
      if (match) {
        const [, day, month, year] = match;
        const fullYear = year.length === 2 ? `20${year}` : year;
        dateStr = `${day.padStart(2, '0')}/${month.padStart(2, '0')}/${fullYear}`;
      }
    }

    setReportDate(dateStr);

    // CORRECTION: Merger les données au lieu de les écraser
    // Garder les transactions des autres pays, remplacer uniquement celles du pays importé
    setGroupedTransactions(prevTransactions => {
      // Supprimer les anciennes données du même pays (au cas où réimport)
      const otherCountries = prevTransactions.filter(group => group.country !== selectedCountry);
      // Ajouter les nouvelles données du pays importé
      const merged = [...otherCountries, ...grouped];

      console.log(`Import ${getCountryInfo(selectedCountry).name}: ${grouped.length} SKUs ajoutés/mis à jour`);
      console.log(`Total après merge: ${merged.length} SKUs (${otherCountries.length} SKUs conservés des autres pays)`);

      return merged;
    });

    setHasData(true);

    // Fermer l'interface d'import additionnel si elle était ouverte
    setShowAdditionalImport(false);

    // Définir le filtre sur le pays des données importées pour commencer
    setSelectedCountryFilter(selectedCountry);
    // En vue pays spécifique, toujours montrer le tableau détaillé
    setShowDetailedTable(true);
  };

  const handleResetAll = () => {
    if (window.confirm('⚠️ ATTENTION: Cette action va effacer TOUTES les données importées (tous les pays).\n\nÊtes-vous sûr de vouloir continuer ?')) {
      setHasData(false);
      setGroupedTransactions([]);
      setImportPeriod(null);
      setSelectedCountryFilter(ALL_COUNTRIES_FILTER);
      setShowDetailedTable(true);
      setShowAdditionalImport(false);
    }
  };

  const fmtDate = (iso) => {
    if (!iso) return '';
    const [y, m, d] = iso.split('-');
    return `${d}/${m}/${y}`;
  };

  const getDbFilterDates = () => {
    const sub = (n) => {
      const d = new Date();
      d.setDate(d.getDate() - n);
      return d.toISOString().slice(0, 10);
    };
    switch (dbFilter.mode) {
      case 'yesterday': return { start: sub(1), end: sub(1) };
      case '7days':     return { start: sub(7), end: sub(1) };
      case '30days':    return { start: sub(30), end: sub(1) };
      case 'custom':    return { start: dbFilter.start, end: dbFilter.end };
      default:          return { start: sub(1), end: sub(1) };
    }
  };

  const handleLoadFromDatabase = async () => {
    try {
      setIsLoading(true);
      setDbEmptyMessage(null);
      const { start, end } = getDbFilterDates();
      if (!start || !end) return;
      const response = await fetch(`/api/transactions/summary?start=${start}&end=${end}`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const { groups, startDate, endDate } = await response.json();

      if (groups.length === 0) {
        setDbEmptyMessage(`Aucune donnée pour cette période (${fmtDate(start)}${start !== end ? ` → ${fmtDate(end)}` : ''}).\nLancez un import depuis l'onglet "Import automatique".`);
        return;
      }

      const formatted = groups.map(g => ({
        sku: g.sku,
        country: g.country,
        title: '',
        totalCa: g.totalCa,
        totalUnits: g.totalUnits,
        totalOrders: g.totalOrders,
        prixUnitaire: g.totalUnits > 0 ? g.totalCa / g.totalUnits : 0,
        transactions: [],
      }));

      setGroupedTransactions(formatted);
      setImportPeriod(startDate && endDate ? { startDate, endDate } : null);
      setHasData(true);
      setSelectedCountryFilter(ALL_COUNTRIES_FILTER);
      setShowDetailedTable(true);
      setShowAdditionalImport(false);
    } catch (error) {
      console.error('Failed to load from database:', error);
      alert('Erreur lors du chargement depuis la base de données : ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleProductCostChange = (compositeKey, value) => {
    const cleanValue = value.toString().replace(',', '.');
    setProductCosts(prev => ({
      ...prev,
      [compositeKey]: cleanValue
    }));

    debounceApiCall(`product_cost_${compositeKey}`, async () => {
      try {
        await apiService.updateProductField(compositeKey, 'product_cost_ht', parseFloat(cleanValue) || 0);
      } catch (error) {
        console.error('Failed to update product cost:', error);
      }
    });
  };

  const handleShippingCostChange = (compositeKey, value) => {
    const cleanValue = value.toString().replace(',', '.');
    setShippingCosts(prev => ({
      ...prev,
      [compositeKey]: cleanValue
    }));

    debounceApiCall(`shipping_cost_${compositeKey}`, async () => {
      try {
        await apiService.updateProductField(compositeKey, 'shipping_cost_ht', parseFloat(cleanValue) || 0);
      } catch (error) {
        console.error('Failed to update shipping cost:', error);
      }
    });
  };

  const handleSupplyCostChange = (compositeKey, value) => {
    const cleanValue = value.toString().replace(',', '.');
    setSupplyCosts(prev => ({
      ...prev,
      [compositeKey]: cleanValue
    }));

    debounceApiCall(`supply_cost_${compositeKey}`, async () => {
      try {
        await apiService.updateProductField(compositeKey, 'supply_cost_ht', parseFloat(cleanValue) || 0);
      } catch (error) {
        console.error('Failed to update supply cost:', error);
      }
    });
  };

  const handleCommissionRateChange = (compositeKey, value) => {
    const cleanValue = value.toString().replace(',', '.');
    setCommissionRates(prev => ({
      ...prev,
      [compositeKey]: cleanValue
    }));

    debounceApiCall(`commission_${compositeKey}`, async () => {
      try {
        await apiService.updateProductField(compositeKey, 'commission_rate', parseFloat(cleanValue) || 15);
      } catch (error) {
        console.error('Failed to update commission rate:', error);
      }
    });
  };

  const handleFulfillmentTypeChange = async (compositeKey, isFBA) => {
    const fulfillmentType = isFBA ? 'FBA' : 'FBM';
    setFulfillmentTypes(prev => ({
      ...prev,
      [compositeKey]: fulfillmentType
    }));

    // Update via API
    try {
      await apiService.updateProductField(compositeKey, 'fulfillment_type', fulfillmentType);
    } catch (error) {
      console.error('Failed to update fulfillment type:', error);
    }
  };

  // Fonction pour calculer automatiquement le payout unitaire (avec nouvelle formule HT)
  const calculateUnitPayout = (prixUnitaire, commissionRate, shippingCostTTC, isFBA, vatDivisor = 1.20) => {
    if (!prixUnitaire || commissionRate === undefined || commissionRate === '') return 0;

    // Prix TTC / vatDivisor = CA HT par unité
    const caHT = prixUnitaire / vatDivisor;

    // Commission HT = Prix TTC × Taux Commission (%)
    const commissionHT = prixUnitaire * (parseFloat(commissionRate) / 100);

    // Frais numériques HT = (Commission HT + frais port Amazon HT si FBA) × 3%
    let baseForNumericFees = commissionHT;
    if (isFBA && shippingCostTTC) {
      const shippingCostHT = parseFloat(shippingCostTTC) / vatDivisor;
      baseForNumericFees += shippingCostHT;
    }
    
    const fraisNumeriquesHT = baseForNumericFees * 0.03;
    
    // Payout HT = CA HT - Commission HT - Frais numériques HT
    return caHT - commissionHT - fraisNumeriquesHT;
  };

  const calculateTotalProfit = (filteredTransactions = null) => {
    const transactionsToProcess = filteredTransactions || groupedTransactions;

    let totalProfit = 0;
    let totalPayout = 0;
    let totalShippingCost = 0;
    let totalSupplyCost = 0;
    let totalProductCost = 0;
    let totalCa = 0;

    transactionsToProcess.forEach(group => {
      const compositeKey = getCompositeKey(group.country, group.sku);
      const prixUnitaire = group.prixUnitaire || 0;
      const commissionRate = parseFloat(commissionRates[compositeKey] || 0);
      const isFBA = fulfillmentTypes[compositeKey] === 'FBA';
      const shippingCostTTC = parseFloat(shippingCosts[compositeKey] || 0);

      // Toujours utiliser le payout calculé automatiquement (avec conversion TTC→HT pour FBA)
      const unitPayout = calculateUnitPayout(prixUnitaire, commissionRate, shippingCostTTC, isFBA, getVatDivisor(COUNTRY_TO_MARKETPLACE[group.country]));

      const payout = unitPayout * group.totalUnits;
      const productCost = parseFloat(productCosts[compositeKey] || 0) * group.totalUnits;

      // Calcul différent selon FBA/FBM (toujours avec les coûts TTC saisis)
      const shippingCost = isFBA
        ? shippingCostTTC * group.totalUnits    // FBA: par unité (TTC)
        : shippingCostTTC * group.totalOrders;  // FBM: par commande (TTC)

      const supplyCost = isFBA
        ? parseFloat(supplyCosts[compositeKey] || 0) * group.totalUnits      // FBA: par unité
        : parseFloat(supplyCosts[compositeKey] || 0) * group.totalOrders;    // FBM: par commande

      const profit = payout - productCost - shippingCost - supplyCost;
      totalProfit += profit;
      totalPayout += payout;
      totalShippingCost += shippingCost;
      totalSupplyCost += supplyCost;
      totalProductCost += productCost;
      totalCa += group.totalCa;
    });

    // Calcul de la marge globale en pourcentage (basée sur le CA HT, TVA par pays)
    const totalCaHT = transactionsToProcess.reduce(
      (sum, group) => sum + (group.totalCa / getVatDivisor(COUNTRY_TO_MARKETPLACE[group.country])),
      0
    );
    const totalMarginPercent = totalCaHT > 0 ? (totalProfit / totalCaHT * 100) : 0;

    return {
      totalProfit,
      totalPayout,
      totalShippingCost,
      totalSupplyCost,
      totalProductCost,
      totalCa,
      totalCaHT,
      totalMarginPercent
    };
  };

  // Trier : pays groupés par total unités décroissant, puis SKU par unités décroissant dans chaque groupe
  const getSortedTransactions = (transactions) => {
    const countryUnits = {};
    transactions.forEach(g => {
      countryUnits[g.country] = (countryUnits[g.country] || 0) + g.totalUnits;
    });
    return [...transactions].sort((a, b) => {
      const byCountry = (countryUnits[b.country] || 0) - (countryUnits[a.country] || 0);
      return byCountry !== 0 ? byCountry : b.totalUnits - a.totalUnits;
    });
  };

  // Filtrer les transactions selon le pays sélectionné
  const getFilteredTransactions = () => {
    const base = selectedCountryFilter === ALL_COUNTRIES_FILTER
      ? groupedTransactions
      : groupedTransactions.filter(group => group.country === selectedCountryFilter);
    return getSortedTransactions(base);
  };

  // Calculer les totaux par pays pour la vue "Tous les pays"
  const calculateCountryTotals = () => {
    if (selectedCountryFilter !== ALL_COUNTRIES_FILTER) {
      return {};
    }

    const countryTotals = {};

    groupedTransactions.forEach(group => {
      const country = group.country;
      if (!countryTotals[country]) {
        countryTotals[country] = {
          totalCa: 0,
          totalProfit: 0,
          totalCaHT: 0,
          country: country
        };
      }

      const compositeKey = getCompositeKey(group.country, group.sku);
      const prixUnitaire = group.prixUnitaire || 0;
      const commissionRate = parseFloat(commissionRates[compositeKey] || 0);
      const isFBA = fulfillmentTypes[compositeKey] === 'FBA';
      const shippingCostTTC = parseFloat(shippingCosts[compositeKey] || 0);

      // Calculer le profit pour ce groupe
      const unitPayout = calculateUnitPayout(prixUnitaire, commissionRate, shippingCostTTC, isFBA, getVatDivisor(COUNTRY_TO_MARKETPLACE[group.country]));
      const payout = unitPayout * group.totalUnits;
      const productCost = parseFloat(productCosts[compositeKey] || 0) * group.totalUnits;

      const shippingCost = isFBA
        ? shippingCostTTC * group.totalUnits
        : shippingCostTTC * group.totalOrders;

      const supplyCost = isFBA
        ? parseFloat(supplyCosts[compositeKey] || 0) * group.totalUnits
        : parseFloat(supplyCosts[compositeKey] || 0) * group.totalOrders;

      const profit = payout - productCost - shippingCost - supplyCost;

      countryTotals[country].totalCa += group.totalCa;
      countryTotals[country].totalProfit += profit;
      countryTotals[country].totalCaHT = countryTotals[country].totalCa / getVatDivisor(COUNTRY_TO_MARKETPLACE[country]);
    });

    // Trier par CA décroissant
    return Object.values(countryTotals).sort((a, b) => b.totalCa - a.totalCa);
  };

  const filteredTransactions = getFilteredTransactions();
  const countryTotals = calculateCountryTotals();
  const { totalProfit, totalPayout, totalShippingCost, totalSupplyCost, totalProductCost, totalCa, totalCaHT, totalMarginPercent } = calculateTotalProfit(filteredTransactions);

  const resetCosts = async () => {
    if (window.confirm('Êtes-vous sûr de vouloir réinitialiser tous les coûts et payouts ?')) {
      try {
        await apiService.resetAllData();
        
        // Reset local state
        setProductCosts({});
        setShippingCosts({});
        setSupplyCosts({});
        setCommissionRates({});
        setFulfillmentTypes({});
        
        alert('Toutes les données ont été réinitialisées avec succès.');
      } catch (error) {
        console.error('Failed to reset data:', error);
        alert('Erreur lors de la réinitialisation des données.');
      }
    }
  };

  // Fonction pour exporter la configuration
  const handleExportConfig = async () => {
    try {
      // Utiliser les données actuelles avec clés composites
      const exportData = {
        productCosts,
        shippingCosts,
        supplyCosts,
        commissionRates,
        fulfillmentTypes,
        exportDate: new Date().toISOString(),
        version: '2.0', // Nouvelle version pour format multi-pays
        format: 'multi-country'
      };

      const dataStr = JSON.stringify(exportData, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });

      // Créer un nom de fichier avec la date
      const today = new Date();
      const dateStr = `${today.getFullYear()}-${(today.getMonth() + 1).toString().padStart(2, '0')}-${today.getDate().toString().padStart(2, '0')}`;
      const fileName = `profits-calculator-multi-pays-export-${dateStr}.json`;

      // Télécharger le fichier
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      alert('Configuration exportée avec succès !');
    } catch (error) {
      console.error('Failed to export configuration:', error);
      alert('Erreur lors de l\'export de la configuration.');
    }
  };

  // Fonction pour importer la configuration
  const handleImportConfig = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const config = JSON.parse(e.target.result);
        
        // Vérifier que c'est bien un fichier de config
        if (!config.productCosts && !config.commissionRates && !config.shippingCosts) {
          alert('Fichier de configuration invalide');
          return;
        }

        // Check if this is an old format that needs migration
        if (config.appVersion === '1.0' || config.version === '1.0') {
          try {
            // Import via migration endpoint
            const response = await fetch('/api/migrate/localStorage', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify(config)
            });
            
            if (!response.ok) {
              throw new Error('Migration failed');
            }
            
            const result = await response.json();
            console.log('Import successful:', result);
            
            // Reload configuration
            await loadConfiguration();
            
            alert(`Configuration importée avec succès !\n${result.migrated_products} produits importés.`);
          } catch (apiError) {
            console.log('API import failed, using local merge:', apiError);
            // Fallback to local merge logic
            await mergeConfigurationLocally(config);
          }
        } else {
          // New format - direct merge
          await mergeConfigurationLocally(config);
        }

      } catch (error) {
        alert('Erreur lors de la lecture du fichier de configuration');
        console.error('Erreur import config:', error);
      }
    };

    reader.readAsText(file);
    // Reset l'input pour permettre de réimporter le même fichier
    event.target.value = '';
  };
  
  const mergeConfigurationLocally = async (config) => {
    let importCount = 0;

    // Détecter le format et migrer si nécessaire
    const isOldFormat = config.version === '1.0' || config.appVersion === '1.0' || !config.version;
    let configToImport = config;

    if (isOldFormat) {
      console.log('Détection d\'un ancien format, migration vers clés composites...');
      configToImport = {
        productCosts: {},
        shippingCosts: {},
        supplyCosts: {},
        commissionRates: {},
        fulfillmentTypes: {}
      };

      // Migrer les données de l'ancien format vers le nouveau
      Object.entries(config).forEach(([fieldType, data]) => {
        if (data && typeof data === 'object' && ['productCosts', 'shippingCosts', 'supplyCosts', 'commissionRates', 'fulfillmentTypes'].includes(fieldType)) {
          const { migratedData } = migrateDataToComposite(data, DEFAULT_COUNTRY);
          configToImport[fieldType] = migratedData;
        }
      });
    }

    // Import intelligent : seulement dans les cases vides ou à zéro
    const mergeData = async (currentData, newData, setterFunction, fieldName) => {
      if (!newData) return 0;

      const merged = { ...currentData };
      let localCount = 0;
      const updates = [];

      Object.entries(newData).forEach(([key, value]) => {
        // Importer seulement si la valeur actuelle est vide, undefined, ou "0"
        const currentValue = currentData[key];
        if (!currentValue || currentValue === '' || currentValue === '0' || currentValue === 0) {
          merged[key] = value;
          localCount++;

          // Prepare API update
          if (fieldName) {
            updates.push({ sku: key, field: fieldName, value });
          }
        }
      });

      if (localCount > 0) {
        setterFunction(merged);

        // Update via API if possible
        if (updates.length > 0) {
          try {
            await apiService.updateProductCosts(updates);
          } catch (error) {
            console.error('Failed to update via API:', error);
          }
        }
      }

      return localCount;
    };

    // Merger chaque type de données
    importCount += await mergeData(productCosts, configToImport.productCosts, setProductCosts, 'product_cost_ht') || 0;
    importCount += await mergeData(shippingCosts, configToImport.shippingCosts, setShippingCosts, 'shipping_cost_ht') || 0;
    importCount += await mergeData(supplyCosts, configToImport.supplyCosts, setSupplyCosts, 'supply_cost_ht') || 0;
    importCount += await mergeData(commissionRates, configToImport.commissionRates, setCommissionRates, 'commission_rate') || 0;
    importCount += await mergeData(fulfillmentTypes, configToImport.fulfillmentTypes, setFulfillmentTypes, 'fulfillment_type') || 0;

    if (importCount > 0) {
      const formatMessage = isOldFormat ?
        `Configuration importée avec succès !\n${importCount} valeurs ont été ajoutées dans les champs vides.\n\nℹ️ Ancien format détecté et migré vers le format multi-pays.` :
        `Configuration importée avec succès !\n${importCount} valeurs ont été ajoutées dans les champs vides.`;
      alert(formatMessage);
    } else {
      alert('Aucune donnée importée.\nToutes les valeurs du fichier existent déjà ou les champs sont déjà remplis.');
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <h1 className="text-2xl font-bold mb-4">Calculateur de Bénéfices Amazon - Multi-Pays</h1>

      {!hasData ? (
        <div>
          <FileUpload onDataLoaded={handleDataLoaded} />
          <div className="mt-4 text-center">
            <p className="text-gray-500 mb-3">— ou —</p>
            <div className="inline-flex flex-wrap items-center gap-2 justify-center">
              <select
                value={dbFilter.mode}
                onChange={e => { setDbFilter(prev => ({ ...prev, mode: e.target.value })); setDbEmptyMessage(null); }}
                className="border border-gray-300 rounded-md px-3 py-2 text-sm bg-white"
              >
                <option value="yesterday">Hier</option>
                <option value="7days">7 derniers jours</option>
                <option value="30days">30 derniers jours</option>
                <option value="custom">Plage libre</option>
              </select>
              {dbFilter.mode === 'custom' && (
                <>
                  <input type="date" value={dbFilter.start} onChange={e => setDbFilter(prev => ({ ...prev, start: e.target.value }))}
                    className="border border-gray-300 rounded-md px-3 py-2 text-sm" />
                  <input type="date" value={dbFilter.end} onChange={e => setDbFilter(prev => ({ ...prev, end: e.target.value }))}
                    className="border border-gray-300 rounded-md px-3 py-2 text-sm" />
                </>
              )}
              <button
                onClick={handleLoadFromDatabase}
                disabled={isLoading || (dbFilter.mode === 'custom' && (!dbFilter.start || !dbFilter.end))}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium disabled:opacity-50"
              >
                {isLoading ? 'Chargement…' : '📡 Charger les données auto-importées'}
              </button>
            </div>
            {dbEmptyMessage && (
              <p className="mt-3 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-4 py-2 inline-block">
                {dbEmptyMessage}
              </p>
            )}
          </div>
        </div>
      ) : (
        <>
          {/* Interface d'import additionnel */}
          {showAdditionalImport && (
            <div className="mb-4 p-4 bg-blue-50 border-2 border-blue-300 rounded-lg">
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-lg font-bold text-blue-800">Importer un pays supplémentaire</h3>
                <button
                  onClick={() => setShowAdditionalImport(false)}
                  className="px-3 py-1 bg-gray-500 text-white rounded hover:bg-gray-600"
                >
                  Annuler
                </button>
              </div>
              <FileUpload onDataLoaded={handleDataLoaded} />
            </div>
          )}

          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setShowAdditionalImport(true)}
                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 font-medium"
              >
                + Importer un autre pays
              </button>

              <button
                onClick={handleResetAll}
                className="px-4 py-2 bg-orange-600 text-white rounded hover:bg-orange-700"
              >
                ⚠️ Analyser un autre fichier
              </button>

              <div className="flex items-center gap-2 flex-wrap">
                <select
                  value={dbFilter.mode}
                  onChange={e => setDbFilter(prev => ({ ...prev, mode: e.target.value }))}
                  className="border border-gray-300 rounded px-2 py-1.5 text-sm bg-white"
                >
                  <option value="yesterday">Hier</option>
                  <option value="7days">7 jours</option>
                  <option value="30days">30 jours</option>
                  <option value="custom">Plage libre</option>
                </select>
                {dbFilter.mode === 'custom' && (
                  <>
                    <input type="date" value={dbFilter.start} onChange={e => setDbFilter(prev => ({ ...prev, start: e.target.value }))}
                      className="border border-gray-300 rounded px-2 py-1.5 text-sm" />
                    <input type="date" value={dbFilter.end} onChange={e => setDbFilter(prev => ({ ...prev, end: e.target.value }))}
                      className="border border-gray-300 rounded px-2 py-1.5 text-sm" />
                  </>
                )}
                <button
                  onClick={handleLoadFromDatabase}
                  disabled={isLoading || (dbFilter.mode === 'custom' && (!dbFilter.start || !dbFilter.end))}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                >
                  {isLoading ? 'Chargement…' : '📡 Données auto-importées'}
                </button>
                {importPeriod && (
                  <span className="text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded px-2 py-1 whitespace-nowrap">
                    {fmtDate(importPeriod.startDate) === fmtDate(importPeriod.endDate)
                      ? fmtDate(importPeriod.startDate)
                      : `${fmtDate(importPeriod.startDate)} → ${fmtDate(importPeriod.endDate)}`}
                  </span>
                )}
              </div>

              {/* Filtre par pays */}
              <div className="flex items-center gap-2">
                <label className="text-gray-700 font-medium">Filtrer par pays :</label>
                <select
                  value={selectedCountryFilter}
                  onChange={(e) => {
                    setSelectedCountryFilter(e.target.value);
                    // En vue pays spécifique, toujours montrer le tableau détaillé
                    if (e.target.value !== ALL_COUNTRIES_FILTER) {
                      setShowDetailedTable(true);
                    } else {
                      // En vue "Tous les pays", masquer le tableau détaillé par défaut
                      setShowDetailedTable(false);
                    }
                  }}
                  className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                >
                  <option value={ALL_COUNTRIES_FILTER}>🌍 Tous les pays</option>
                  {COUNTRIES.filter(country =>
                    groupedTransactions.some(group => group.country === country.code)
                  ).map(country => (
                    <option key={country.code} value={country.code}>
                      {country.flag} {country.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex gap-2">
              <button
                onClick={handleExportConfig}
                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
              >
                Exporter Config
              </button>

              <button
                onClick={() => fileInputRef.current?.click()}
                className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700"
              >
                Importer Config
              </button>

              <input
                ref={fileInputRef}
                type="file"
                accept=".json"
                onChange={handleImportConfig}
                className="hidden"
              />

              <button
                onClick={resetCosts}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
              >
                Réinitialiser
              </button>
            </div>
          </div>
          
          {/* A. RÉSUMÉ GLOBAL (toujours visible) */}
          <div className="bg-gray-100 p-4 rounded mb-6">
            <div className="flex justify-between items-center mb-2">
              <h2 className="text-xl font-bold">
                {selectedCountryFilter === ALL_COUNTRIES_FILTER
                  ? 'Résumé Global - Tous Pays'
                  : `Résumé ${getCountryInfo(selectedCountryFilter).flag} ${getCountryInfo(selectedCountryFilter).name}`
                }
              </h2>
              {reportDate && <span className="text-gray-600 font-medium">Rapport du {reportDate}</span>}
            </div>
            <div className="grid grid-cols-6 gap-4">
              <div className="bg-white p-3 rounded shadow">
                <h3 className="text-gray-600">CA Total HT</h3>
                <p className="text-xl font-bold text-green-600">{totalCaHT.toFixed(2)} €</p>
              </div>
              <div className="bg-white p-3 rounded shadow">
                <h3 className="text-gray-600">Total Frais de Port</h3>
                <p className="text-xl font-bold text-red-600">{totalShippingCost.toFixed(2)} €</p>
              </div>
              <div className="bg-white p-3 rounded shadow">
                <h3 className="text-gray-600">Total Fournitures</h3>
                <p className="text-xl font-bold text-red-600">{totalSupplyCost.toFixed(2)} €</p>
              </div>
              <div className="bg-white p-3 rounded shadow">
                <h3 className="text-gray-600">Total Réassort Produit</h3>
                <p className="text-xl font-bold text-red-600">{totalProductCost.toFixed(2)} €</p>
              </div>
              <div className="bg-white p-3 rounded shadow">
                <h3 className="text-gray-600">Bénéfice Net</h3>
                <p className="text-xl font-bold text-blue-600">{totalProfit.toFixed(2)} €</p>
              </div>
              <div className="bg-white p-3 rounded shadow">
                <h3 className="text-gray-600">Marge Globale</h3>
                <p className="text-xl font-bold" style={{ color: getMarginColor(totalMarginPercent) }}>
                  {totalMarginPercent.toFixed(2)} %
                </p>
              </div>
            </div>
          </div>

          {/* B. COMPARATIF PAR PAYS (visible uniquement en vue "Tous les pays") */}
          {selectedCountryFilter === ALL_COUNTRIES_FILTER && countryTotals.length > 0 && (
            <div className="bg-white rounded shadow mb-6">
              <h3 className="text-lg font-bold p-4 border-b">📊 Comparatif par Pays</h3>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="border p-3 text-left">Pays</th>
                      <th className="border p-3 text-right">CA HT (€)</th>
                      <th className="border p-3 text-right">Bénéfice (€)</th>
                      <th className="border p-3 text-right">Marge (%)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {countryTotals.map((countryData, index) => {
                      const countryInfo = getCountryInfo(countryData.country);
                      const marginPercent = countryData.totalCaHT > 0 ? (countryData.totalProfit / countryData.totalCaHT * 100) : 0;
                      return (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="border p-3">
                            <span className="text-lg mr-2">{countryInfo.flag}</span>
                            <span className="font-medium">{countryInfo.name}</span>
                          </td>
                          <td className="border p-3 text-right font-bold">{countryData.totalCaHT.toFixed(2)}</td>
                          <td className="border p-3 text-right font-bold" style={{ color: countryData.totalProfit >= 0 ? '#059669' : '#DC2626' }}>
                            {countryData.totalProfit.toFixed(2)}
                          </td>
                          <td className="border p-3 text-right font-bold" style={{ color: getMarginColor(marginPercent) }}>
                            {marginPercent.toFixed(2)}%
                          </td>
                        </tr>
                      );
                    })}
                    {/* Ligne TOTAL */}
                    <tr className="bg-gray-100 font-bold">
                      <td className="border p-3">🌍 TOTAL</td>
                      <td className="border p-3 text-right">{totalCaHT.toFixed(2)}</td>
                      <td className="border p-3 text-right" style={{ color: totalProfit >= 0 ? '#059669' : '#DC2626' }}>
                        {totalProfit.toFixed(2)}
                      </td>
                      <td className="border p-3 text-right" style={{ color: getMarginColor(totalMarginPercent) }}>
                        {totalMarginPercent.toFixed(2)}%
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* C. TABLEAU DÉTAILLÉ */}
          {selectedCountryFilter === ALL_COUNTRIES_FILTER && (
            <div className="mb-4">
              <button
                onClick={() => setShowDetailedTable(!showDetailedTable)}
                className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 flex items-center gap-2"
              >
                {showDetailedTable ? 'Masquer le détail ▲' : 'Afficher le détail complet ▼'}
              </button>
            </div>
          )}

          {((selectedCountryFilter !== ALL_COUNTRIES_FILTER) || showDetailedTable) && (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse mb-4">
                <thead>
                  <tr className="bg-gray-200">
                    {selectedCountryFilter === ALL_COUNTRIES_FILTER && (
                      <th className="border p-2">Pays</th>
                    )}
                    <th className="border p-2">SKU</th>
                    <th className="border p-2">Titre</th>
                    <th className="border p-2">Type</th>
                    <th className="border p-2">Unités</th>
                    <th className="border p-2">Commandes</th>
                    <th className="border p-2">CA Total (€)</th>
                    <th className="border p-2">Prix Unitaire (€)</th>
                    <th className="border p-2">Commission (%)</th>
                    <th className="border p-2">Payout/Unité (€)</th>
                    <th className="border p-2">Payout Total (€)</th>
                    <th className="border p-2">Coût/Unité (€ HT)</th>
                    <th className="border p-2">Port/Commande (€ HT)</th>
                    <th className="border p-2">Fournitures/Commande (€ HT)</th>
                    <th className="border p-2">Bénéfice (€)</th>
                    <th className="border p-2">Marge (%)</th>
                  </tr>
                </thead>
                <tbody>
                  {(filteredTransactions || []).map((group, index) => {
                    const compositeKey = getCompositeKey(group.country, group.sku);
                    const prixUnitaire = group.prixUnitaire || 0;
                    const commissionRate = parseFloat(commissionRates[compositeKey] || 0);
                    const isFBA = fulfillmentTypes[compositeKey] === 'FBA';
                    const shippingCostTTC = parseFloat(shippingCosts[compositeKey] || 0);

                    // Calculer automatiquement le payout unitaire (avec conversion automatique TTC→HT pour FBA)
                    const calculatedUnitPayout = calculateUnitPayout(prixUnitaire, commissionRate, shippingCostTTC, isFBA, getVatDivisor(COUNTRY_TO_MARKETPLACE[group.country]));

                    // Toujours utiliser le payout calculé
                    const unitPayout = calculatedUnitPayout;

                    const totalPayout = unitPayout * group.totalUnits;
                    const productCost = parseFloat(productCosts[compositeKey] || 0);
                    const supplyCost = parseFloat(supplyCosts[compositeKey] || 0);
                    const totalProductCost = productCost * group.totalUnits;

                    // Calcul différent selon FBA/FBM (avec les coûts TTC saisis)
                    const totalShippingCost = isFBA
                      ? shippingCostTTC * group.totalUnits    // FBA: par unité (TTC)
                      : shippingCostTTC * group.totalOrders;  // FBM: par commande (TTC)

                    const totalSupplyCost = isFBA
                      ? supplyCost * group.totalUnits      // FBA: par unité
                      : supplyCost * group.totalOrders;    // FBM: par commande

                    const profit = totalPayout - totalProductCost - totalShippingCost - totalSupplyCost;

                    // Calcul de la marge en pourcentage (basée sur le CA HT)
                    const caHT = group.totalCa / getVatDivisor(COUNTRY_TO_MARKETPLACE[group.country]);
                    const marginPercent = caHT > 0 ? (profit / caHT * 100) : 0;

                    const countryInfo = getCountryInfo(group.country);

                    return (
                      <tr key={index} className="hover:bg-gray-100">
                        {selectedCountryFilter === ALL_COUNTRIES_FILTER && (
                          <td className="border p-2 text-center">
                            <span className="text-lg">{countryInfo.flag}</span>
                          </td>
                        )}
                        <td className="border p-2">{group.sku}</td>
                        <td className="border p-2">
                          {(group.title || '').length > 40
                            ? `${(group.title || '').substring(0, 40)}...`
                            : (group.title || '')}
                        </td>
                        <td className="border p-2">
                          <div className="flex items-center justify-center">
                            <label className="relative inline-flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={isFBA}
                                onChange={(e) => handleFulfillmentTypeChange(compositeKey, e.target.checked)}
                                className="sr-only peer"
                              />
                              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                              <span className="ml-2 text-xs font-medium text-gray-700">
                                {isFBA ? 'FBA' : 'FBM'}
                              </span>
                            </label>
                          </div>
                        </td>
                        <td className="border p-2 text-center">{group.totalUnits}</td>
                        <td className="border p-2 text-center">{group.totalOrders}</td>
                        <td className="border p-2 text-right">{group.totalCa.toFixed(2)}</td>
                        <td className="border p-2 text-right font-bold text-blue-600">{prixUnitaire.toFixed(2)}</td>
                        <td className="border p-2">
                          <input
                            type="text"
                            value={commissionRates[compositeKey] || ''}
                            onChange={(e) => handleCommissionRateChange(compositeKey, e.target.value)}
                            className="w-full text-right p-1 border rounded"
                            placeholder="0.00"
                          />
                        </td>
                        <td className="border p-2 text-right">
                          {calculatedUnitPayout.toFixed(2)}
                        </td>
                        <td className="border p-2 text-right">{totalPayout.toFixed(2)}</td>
                        <td className="border p-2">
                          <input
                            type="text"
                            value={productCosts[compositeKey] || ''}
                            onChange={(e) => handleProductCostChange(compositeKey, e.target.value)}
                            className="w-full text-right p-1 border rounded"
                            placeholder="0.00"
                          />
                        </td>
                        <td className="border p-2">
                          <input
                            type="text"
                            value={shippingCosts[compositeKey] || ''}
                            onChange={(e) => handleShippingCostChange(compositeKey, e.target.value)}
                            className="w-full text-right p-1 border rounded"
                            placeholder="0.00"
                          />
                          <div className="text-xs text-gray-500 mt-1">
                            {isFBA ? 'par unité (HT)' : 'par commande (HT)'}
                          </div>
                        </td>
                        <td className="border p-2">
                          <input
                            type="text"
                            value={supplyCosts[compositeKey] || ''}
                            onChange={(e) => handleSupplyCostChange(compositeKey, e.target.value)}
                            className="w-full text-right p-1 border rounded"
                            placeholder="0.00"
                          />
                          <div className="text-xs text-gray-500 mt-1">
                            {isFBA ? 'par unité (HT)' : 'par commande (HT)'}
                          </div>
                        </td>
                        <td className="border p-2 text-right font-bold">{profit.toFixed(2)}</td>
                        <td className="border p-2 text-right font-bold" style={{ color: getMarginColor(marginPercent) }}>
                          {marginPercent.toFixed(2)}%
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ProfitCalculator;